import { AssignmentOutlined, Inventory2Outlined } from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Drawer,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import EquipmentTab from "../../components/Warehouse/Tabs/EquipmentTab";
import { usePermissions } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { setTab } from "../../store/UI";
import { UIStore } from "../../types/store";
import RequisitionsTab from "../../components/Warehouse/Tabs/RequisitionsTab";

export default function DetailsWarehouse() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const handleError = useErrorHandler();
  const { hasPermission } = usePermissions();
  const { canEditData } = useAccountPermissions();

  const drawerItems = [
    {
      id: "equipment",
      text: t("warehouse.tabs.equipment"),
      icon: <Inventory2Outlined />,
      permission: "WAREHOUSE_TAB_EQUIPMENT",
    },
    {
      id: "requisitions",
      text: t("warehouse.tabs.requisitions"),
      icon: <AssignmentOutlined />,
      permission: "WAREHOUSE_TAB_REQUISITIONS",
    },
  ].filter((item) => hasPermission(item.permission));

  const tabState = useSelector((state: { ui: UIStore }) => state.ui.tabState);
  const [activeTab, setActiveTab] = useState<string>(() => {
    const saved = String(tabState[location.pathname] ?? "");
    return drawerItems.some((item) => item.id === saved)
      ? saved
      : (drawerItems[0]?.id ?? "equipment");
  });

  const [open, setOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleDrawerToggle = () => {
    setIsTransitioning(true);
    setOpen(!open);
  };

  const handleTabChange = (_event: React.SyntheticEvent | React.MouseEvent, newValue: string) => {
    setActiveTab(newValue);
    dispatch(setTab({ page: location.pathname, tab: newValue }));
  };

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar>
        <IconButton
          color="inherit"
          onClick={handleDrawerToggle}
          edge="start"
          sx={{ marginRight: 2 }}
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
        <Typography sx={{ flex: "1 1 100%" }} variant="h4">
          {t("warehouse.equipment.title")}
        </Typography>
      </Toolbar>

      <Box sx={{ display: "flex" }}>
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: open ? 240 : (theme) => `calc(${theme.spacing(7)} + 1px)`,
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: open
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              position: "relative",
              whiteSpace: "nowrap",
              boxSizing: "border-box",
              width: "inherit",
              overflowX: "hidden",
              border: "none",
              zIndex: "auto",
            },
          }}
          onTransitionEnd={() => setIsTransitioning(false)}
        >
          <List component="nav">
            {drawerItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <Tooltip title={open ? "" : item.text} placement="left">
                  <ListItemButton
                    selected={activeTab === item.id}
                    onClick={(e) => handleTabChange(e, item.id)}
                    sx={{ minHeight: 48, justifyContent: "initial", px: 2.5, alignItems: "center" }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        opacity: open ? 1 : 0,
                        transition: (theme) => theme.transitions.create("opacity"),
                        whiteSpace: open && !isTransitioning ? "normal" : "nowrap",
                        overflow: "hidden",
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Drawer>

        <Box sx={{ flexGrow: 1, p: 3 }}>
          {hasPermission("WAREHOUSE_TAB_EQUIPMENT") && (
            <Fade in={activeTab === "equipment"} timeout={300} unmountOnExit>
              <Box>
                {activeTab === "equipment" && (
                  <EquipmentTab handleError={handleError} canEdit={canEditData} />
                )}
              </Box>
            </Fade>
          )}

          {hasPermission("WAREHOUSE_TAB_REQUISITIONS") && (
            <Fade in={activeTab === "requisitions"} timeout={300} unmountOnExit>
              <Box>
                {activeTab === "requisitions" && (
                  <RequisitionsTab handleError={handleError} canEdit={canEditData} />
                )}
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Paper>
  );
}